import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { ReactComponent as IconWarning } from "@assets/Icons/Icon_Warning.svg";
import { ReactComponent as LogsSVG } from "@assets/Icons/IconLogs.svg";

/* ──────────────────────────────────────────────────────────────────────────────
 * Page-level full-block states for the Audit Logs view.
 *
 * These render INSTEAD OF the filter bar + table — they're for situations
 * where the page can't function at all (no audited products) or the
 * options endpoint failed before we have anything to show.
 *
 * In-table states (loading skeleton, overlay, empty results, list error)
 * stay co-located with `<MainAuditLogs>` because they need to share state
 * with the table.
 * ──────────────────────────────────────────────────────────────────────────── */

interface FullBlockShellProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  primaryAction?: { label: string; onClick: () => void };
}

const FullBlockShell: React.FC<FullBlockShellProps> = ({
  icon,
  title,
  description,
  primaryAction,
}) => (
  <div className="flex h-full items-center justify-center p-6">
    <div className="w-full max-w-md">
      <GenericCard
        isCollapsible={false}
        bodyContent={
          <div className="flex flex-col items-center gap-4 p-6 text-center">
            <div className="inline-flex items-center justify-center rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary p-2">
              {icon}
            </div>
            <h2 className="text-lg font-semibold text-semantic-text-primary">
              {title}
            </h2>
            <div className="text-sm text-semantic-text-secondary">
              {description}
            </div>
            {primaryAction ? (
              <button
                type="button"
                onClick={primaryAction.onClick}
                className="mt-2 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary px-4 py-2 text-sm font-medium text-semantic-text-brand hover:bg-semantic-state-active"
              >
                {primaryAction.label}
              </button>
            ) : null}
          </div>
        }
      />
    </div>
  </div>
);

/**
 * Tenant has authenticated successfully but `filters/options.products` is
 * empty — the backend says no product in the tenant has audit data
 * available. The whole page is blocked because filtering / listing makes
 * no sense without at least one product.
 */
export const NoAuditedProductsState: React.FC = () => (
  <FullBlockShell
    icon={
      <LogsSVG className="h-8 w-8 text-semantic-text-secondary" />
    }
    title="No audit-enabled products"
    description={
      <p>
        Your tenant doesn&apos;t have any products with auditing available
        yet. If you believe this is a mistake, contact your account
        manager.
      </p>
    }
  />
);

/**
 * `filters/options` failed and we have no cached snapshot to fall back to.
 * Without this catalogue we can't build the dropdowns or know what
 * products to authorize, so we block the page until the user retries.
 */
export const FilterOptionsErrorState: React.FC<{
  message: string;
  onRetry: () => void;
}> = ({ message, onRetry }) => (
  <FullBlockShell
    icon={<IconWarning className="h-8 w-8 text-semantic-text-secondary" />}
    title="Couldn't load audit log filters"
    description={
      <>
        <p className="mb-1">
          We couldn&apos;t load the filter catalogue for audit logs.
        </p>
        <p className="text-xs text-semantic-text-tertiary">{message}</p>
      </>
    }
    primaryAction={{ label: "Retry", onClick: onRetry }}
  />
);
